import { Box, Modal, Typography, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { NewButton } from 'components/NewButton';
import { Container, StyledTableContainer } from './styles';
import { newMoneyWithNegative } from 'utils/newMoney';

export interface IPendingChange {
  id: number;
  driverName: string;
  licensePlate: string;
  deliveryDate?: string;
  route?: string; // Apenas para LastMile
  originalCostAdjustment: number;
  newCostAdjustment: number;
  originalDefaultCost?: number;
  newDefaultCost?: number;
  // Para FirstMile que também edita vehicleCost
  originalVehicleCost?: number;
  newVehicleCost?: number;
  // Valor de descarregamento (LastMile)
  originalUnloadingCost?: number;
  newUnloadingCost?: number;
  // Comissão do líder (LastMile)
  originalLeaderCommission?: number;
  newLeaderCommission?: number;
}

interface ConfirmChangesPopupProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  changes: IPendingChange[];
  loading?: boolean;
}

export function ConfirmChangesPopup({ 
  open, 
  onClose, 
  onConfirm, 
  changes, 
  loading = false 
}: ConfirmChangesPopupProps) {
  // Verificar se algum item tem rota (para mostrar/esconder coluna)
  const hasRoute = changes.some(c => c.route);
  // Verificar se algum item tem alteração de vehicleCost (FirstMile)
  const hasVehicleCost = changes.some(c => c.originalVehicleCost !== undefined && c.originalVehicleCost !== c.newVehicleCost);
  // Verificar se algum item tem valor de descarregamento
  const hasUnloadingCost = changes.some(c => c.originalUnloadingCost !== undefined || c.newUnloadingCost !== undefined);
  // Verificar se algum item tem alteração de comissão do líder
  const hasLeaderCommission = changes.some(c => c.originalLeaderCommission !== undefined || c.newLeaderCommission !== undefined);

  return (
    <Modal open={open} onClose={onClose}>
      <Container>
        <Box className="header">
          <Typography variant="h6" component="h2">
            Confirmar Alterações ({changes.length})
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          As seguintes alterações serão aplicadas:
        </Typography>

        <StyledTableContainer>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>Motorista</TableCell>
                <TableCell align="center">Placa</TableCell>
                <TableCell align="center">Data</TableCell>
                {hasRoute && <TableCell align="center">Rota</TableCell>}
                <TableCell align="center">Ajuste</TableCell>
                {hasLeaderCommission && <TableCell align="center">Comissão Líder</TableCell>}
                {hasUnloadingCost && <TableCell align="center">Val. Descarregamento</TableCell>}
                {hasVehicleCost && <TableCell align="center">Valor da Rota</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {changes.map((change) => (
                <TableRow key={change.id}>
                  <TableCell>{change.driverName}</TableCell>
                  <TableCell align="center">{change.licensePlate}</TableCell>
                  <TableCell align="center">{change.deliveryDate || '-'}</TableCell>
                  {hasRoute && <TableCell align="center">{change.route || '-'}</TableCell>}
                  <TableCell align="center">
                    {change.originalCostAdjustment !== change.newCostAdjustment ? (
                      <div className="change-cell">
                        <span className="old-value">R$ {newMoneyWithNegative.apply(change.originalCostAdjustment)}</span>
                        <span className="arrow">→</span>
                        <span className="new-value">R$ {newMoneyWithNegative.apply(change.newCostAdjustment)}</span>
                      </div>
                    ) : (
                      <span>R$ {newMoneyWithNegative.apply(change.newCostAdjustment)}</span>
                    )}
                  </TableCell>
                  {hasLeaderCommission && (
                    <TableCell align="center">
                      {change.originalLeaderCommission !== change.newLeaderCommission ? (
                        <div className="change-cell">
                          <span className="old-value">R$ {newMoneyWithNegative.apply(change.originalLeaderCommission || 0)}</span>
                          <span className="arrow">→</span>
                          <span className="new-value">R$ {newMoneyWithNegative.apply(change.newLeaderCommission || 0)}</span>
                        </div>
                      ) : (
                        <span>R$ {newMoneyWithNegative.apply(change.newLeaderCommission || 0)}</span>
                      )}
                    </TableCell>
                  )}
                  {hasUnloadingCost && (
                    <TableCell align="center">
                      {change.originalUnloadingCost !== change.newUnloadingCost ? (
                        <div className="change-cell">
                          <span className="old-value">R$ {newMoneyWithNegative.apply(change.originalUnloadingCost || 0)}</span>
                          <span className="arrow">→</span>
                          <span className="new-value">R$ {newMoneyWithNegative.apply(change.newUnloadingCost || 0)}</span>
                        </div>
                      ) : (
                        <span>R$ {newMoneyWithNegative.apply(change.newUnloadingCost || 0)}</span>
                      )}
                    </TableCell>
                  )}
                  {hasVehicleCost && (
                    <TableCell align="center">
                      {change.originalVehicleCost !== change.newVehicleCost ? (
                        <div className="change-cell">
                          <span className="old-value">R$ {newMoneyWithNegative.apply(change.originalVehicleCost || 0)}</span>
                          <span className="arrow">→</span>
                          <span className="new-value">R$ {newMoneyWithNegative.apply(change.newVehicleCost || 0)}</span>
                        </div>
                      ) : (
                        <span>R$ {newMoneyWithNegative.apply(change.newVehicleCost || 0)}</span>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>

        <Box className="actions">
          <NewButton 
            variantType="cancel" 
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </NewButton>
          <NewButton 
            variantType="filled" 
            onClick={onConfirm}
            disabled={loading}
          >
            {loading ? 'Salvando...' : 'Confirmar'}
          </NewButton>
        </Box>
      </Container>
    </Modal>
  );
}
